import { memo, useCallback } from 'react';
import { CartContainer, Checkbox } from '@aimsos/ui';

import { MEMBER_STATUS } from '../../constants/copy';
import MemberAvatar from './MemberAvatar';
import StatusBadge from './StatusBadge';

/**
 * One pickable member: avatar, name, email and status. The row owns the click (it is the only
 * interactive element); the checkbox is only the visual state, so it never toggles twice.
 *
 * @param {object} props
 * @param {{ id: string, name: string, email: string, status: string }} props.member Picker member (see `useBuilderMembers`).
 * @param {(memberId: string) => void} props.onPick
 * @param {string} props.ariaLabel Accessible name of the row.
 * @param {boolean} [props.selected] When defined the row shows a checkbox and is a toggle.
 * @param {'md'|'lg'} [props.avatarSize='lg']
 * @param {boolean} [props.showActiveStatus=true] Hide the status badge of active members.
 */
const MemberPickRow = ({ member, onPick, ariaLabel, selected, avatarSize = 'lg', showActiveStatus = true }) => {
  const handleClick = useCallback(() => onPick(member.id), [onPick, member.id]);
  const isToggle = selected !== undefined;

  return (
    <CartContainer
      size="S"
      variant="primary"
      selected={selected}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={isToggle ? selected : undefined}
    >
      <div className="flex min-w-0 items-center gap-3">
        {isToggle ? (
          <span className="pointer-events-none shrink-0" aria-hidden>
            <Checkbox size="small" checked={selected} />
          </span>
        ) : null}
        <MemberAvatar member={member} size={avatarSize} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-content">{member.name}</p>
          <p className="truncate text-xs text-content-muted">{member.email}</p>
        </div>
        {showActiveStatus || member.status !== MEMBER_STATUS.ACTIVE ? <StatusBadge status={member.status} /> : null}
      </div>
    </CartContainer>
  );
};

export default memo(MemberPickRow);
